import { getTenantAuth, tokenNeedsRefresh } from "./TokenManager";

/**
 * Tenant-scoped tokens, cached until they near expiry.
 *
 * WHY THIS EXISTS, AND HOW IT DIFFERS FROM 3A. `useTenantInspection` mints a
 * fresh token on every operation so that a token is never reused across a gap,
 * which deletes the expiry failure class instead of handling it. That trade is
 * right for a view making one request per visit. The email worklist makes one
 * per page click, per filter change and per tenant switch, so minting every
 * time would put an STS round trip in front of each. This cache brings expiry
 * back as a handled case, with `tokenNeedsRefresh`'s ten-minute margin.
 *
 * RETURNS A STRING, NEVER A `selectedTenant`. `getTenantAuth` returns the
 * latter, which is exactly the shape `setSelectedTenant` accepts — and nothing
 * in Main.tsx re-fetches when the app-wide tenant changes, so writing it here
 * would leave the app pointing at one tenant while Departments, Mailboxes and
 * Actions still held another's data. Returning only the string makes that
 * mistake unavailable to callers rather than merely discouraged.
 */
const tokens = new Map<string, string>();

/**
 * In-flight mints, so two callers for the same tenant share one STS request.
 *
 * Cleared in a `finally`, including on rejection — a failed mint must not leave
 * a rejected promise parked in the map for every later caller to receive.
 */
const inFlight = new Map<string, Promise<string>>();

/**
 * A tenant-scoped token, minted if there is not a usable one already.
 *
 * Throws whatever `getTenantAuth` throws — bare `Error("403")` / `Error("401")`,
 * not typed errors. A refusal is NEVER cached: entitlement can be revoked or
 * granted within a session, and a cached refusal would outlive the change.
 */
export async function getCachedTenantToken(tenantKey: string): Promise<string> {
    const cached = tokens.get(tenantKey);
    if (cached && !tokenNeedsRefresh(cached)) {
        return cached;
    }

    const existing = inFlight.get(tenantKey);
    if (existing) {
        return await existing;
    }

    const mint = (async () => {
        const auth = await getTenantAuth(tenantKey);
        tokens.set(tenantKey, auth.token);
        return auth.token;
    })();

    inFlight.set(tenantKey, mint);
    try {
        return await mint;
    } finally {
        inFlight.delete(tenantKey);
    }
}

/** Tests only. Nothing in the app clears the cache. */
export function clearTenantTokenCache(): void {
    tokens.clear();
    inFlight.clear();
}
